import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel, MENSAGEM_ERRO_TECNICO } from '@/lib/mensagemAmigavel';
import type { ParticipacaoCriacao } from './types';

// Sugestões do estado vazio (a conversa começa ao tocar numa delas).
export const CHIPS_INICIAIS = ['Acampamento', 'Show ou luau', 'Quermesse'];

export class ErroCriacao extends Error {
  limite: boolean;

  constructor(mensagem: string, limite = false) {
    super(mensagem);
    this.limite = limite;
  }
}

// Fala com a Edge Function "assistente-criar". Em erro, lê { erro } do corpo
// (quando é um FunctionsHttpError) e devolve uma frase amigável, nunca técnica.
export async function chamarAssistenteCriar<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('assistente-criar', { body });
  if (!error) return data as T;

  let texto = '';
  let limite = false;
  if (error instanceof FunctionsHttpError) {
    limite = error.context?.status === 429;
    try {
      const corpo = await error.context.json();
      if (corpo && typeof corpo.erro === 'string') texto = corpo.erro;
    } catch {
      /* corpo sem JSON: cai na mensagem padrão */
    }
  }
  // A frase que o banco devolve em português passa; qualquer erro técnico
  // vira a frase única (o detalhe fica no console).
  throw new ErroCriacao(
    mensagemAmigavel(texto || error, MENSAGEM_ERRO_TECNICO),
    limite,
  );
}

// "sex, 20/11 · 19h" no horário de Brasília.
export function linhaData(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const fuso = 'America/Sao_Paulo';
  const wd = new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, weekday: 'short' })
    .format(d)
    .replace(/\.$/, '');
  const dm = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  const hh = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    hour: '2-digit',
    hour12: false,
  }).format(d);
  return `${wd}, ${dm} · ${hh}h`;
}

// "20/11" no horário de Brasília.
export function diaMes(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

// Texto do valor de uma participação: "R$ 120,00", "Gratuito" ou "Valor a definir".
export function textoValorParticipacao(p: ParticipacaoCriacao): string {
  if (p.gratuito) return 'Gratuito';
  if (typeof p.valor === 'number' && p.valor > 0) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      p.valor / 100,
    );
  }
  if (p.valor_texto) return p.valor_texto;
  return 'Valor a definir';
}